import { useEffect, useState, type ComponentType } from "react";
import { BookOpen, Download, Calendar, Activity } from "react-feather";
import {
  useT,
  selectHasSeenOnboarding,
  setHasSeenOnboarding,
  selectPrefsPulled,
  useAppDispatch,
  useAppSelector,
} from "@reciperoll/core";

type FeatherIcon = ComponentType<{ className?: string }>;

// The intro slides. Copy lives in the "onboarding.slides.*" catalog; here we only
// pick the icon per slide. Add/remove a slide by editing both together. Keys and
// icons mirror the RN OnboardingGate so the two platforms stay in step.
const SLIDES: { key: string; icon: FeatherIcon }[] = [
  { key: "welcome", icon: BookOpen },
  { key: "save", icon: Download },
  { key: "plan", icon: Calendar },
  { key: "cook", icon: Activity },
];

// First-run intro carousel for web. Shown once the account's preferences have
// been pulled and `hasSeenOnboarding` is still false — dismissed by "Get started"
// or "Skip", after which the shared (account-synced) flag means it never reappears
// (until replayed from Preferences). Rendered as a full-screen overlay over
// bg-paper so it sits above the app; AuthGate only mounts it when signed in.
export function Onboarding() {
  const hasSeen = useAppSelector(selectHasSeenOnboarding);
  // hasSeenOnboarding is account-synced, so decide only once the account's
  // preferences have actually been pulled — until then the local default `false`
  // is meaningless. A brand-new account pulls no prefs row, so the flag stays
  // `false` and the tour shows.
  const prefsPulled = useAppSelector(selectPrefsPulled);
  const dispatch = useAppDispatch();
  const t = useT();
  const [index, setIndex] = useState(0);

  const visible = prefsPulled && !hasSeen;

  // This component stays mounted (returns null when hidden), so reset to the first
  // slide each time it re-opens — otherwise a replay lands on the last page.
  useEffect(() => {
    if (visible) setIndex(0);
  }, [visible]);

  if (!visible) return null;

  const isLast = index === SLIDES.length - 1;
  const slide = SLIDES[index];
  const Icon = slide.icon;

  const finish = () => {
    setIndex(0); // so a later replay opens on the first slide (belt-and-braces
    dispatch(setHasSeenOnboarding(true)); //  with the visibility effect above)
  };
  const goNext = () => (isLast ? finish() : setIndex((i) => i + 1));
  const goPrev = () => setIndex((i) => Math.max(0, i - 1));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-paper p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t("onboarding.slides.welcome.title")}
    >
      <div className="flex w-full max-w-md flex-col rounded-3xl bg-surface p-8 shadow-xl">
        {/* Skip — top-right, hidden on the last slide (the CTA covers it). */}
        <div className="mb-2 flex h-6 items-center justify-end">
          {!isLast && (
            <button
              onClick={finish}
              className="text-sm font-semibold text-ink-faint hover:text-ink cursor-pointer"
            >
              {t("onboarding.skip")}
            </button>
          )}
        </div>

        {/* Current slide. */}
        <div className="flex flex-col items-center px-2 py-6 text-center">
          <div className="mb-8 flex h-24 w-24 items-center justify-center rounded-3xl bg-terracotta/10">
            <Icon className="h-10 w-10 text-terracotta-ink" />
          </div>
          <h2 className="mb-3 text-2xl font-extrabold text-ink">
            {t(`onboarding.slides.${slide.key}.title`)}
          </h2>
          <p className="text-base leading-relaxed text-ink-soft">
            {t(`onboarding.slides.${slide.key}.body`)}
          </p>
        </div>

        {/* Pagination dots — also click-to-jump. */}
        <div className="mb-6 flex items-center justify-center gap-2">
          {SLIDES.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setIndex(i)}
              aria-label={t(`onboarding.slides.${s.key}.title`)}
              aria-current={i === index}
              className={`h-2 rounded-full transition-all cursor-pointer ${
                i === index ? "w-6 bg-terracotta" : "w-2 bg-line hover:bg-ink-faint"
              }`}
            />
          ))}
        </div>

        {/* Prev / primary CTA. Prev is hidden on the first slide. */}
        <div className="flex items-center gap-3">
          {index > 0 && (
            <button
              onClick={goPrev}
              className="rounded-xl border border-line px-5 py-3 text-sm font-bold text-ink-soft hover:bg-surface-2 cursor-pointer"
            >
              {t("common.back")}
            </button>
          )}
          <button
            onClick={goNext}
            className="flex-1 rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors py-3 text-sm font-bold text-white cursor-pointer"
          >
            {isLast ? t("onboarding.getStarted") : t("onboarding.next")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
